import { PlaceInfo } from '@buseditor/types'
import { getZeroRotatedBox } from '@buseditor/utils'
import { useTheme } from '@mui/material'
import Konva from 'konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { Vector2d } from 'konva/lib/types'
import React, { useCallback, useMemo, useRef } from 'react'
import { Group, Image, Rect, Text } from 'react-konva'

export interface BookableSeatProps {
    available: boolean
    isSelected: boolean
    onSelect: (e: KonvaEventObject<MouseEvent | Event>, shape: Konva.Node) => void
    driverImage: CanvasImageSource
    handleHover?: (seat: PlaceInfo, position?: Vector2d) => void
    seatBackSide?: 'left' | 'bottom'
}

export function BookableSeat({ available, isSelected, onSelect: onSelectBase, driverImage, handleHover, seatBackSide = 'left', ...rectProps }: BookableSeatProps & PlaceInfo) {
    const shapeRef = useRef<Konva.Node>(null)
    const theme = useTheme()
    const textBox = useMemo(() => {
        const temp = getZeroRotatedBox({ x: 0, y: 0, width: rectProps.width, height: rectProps.height, rotation: -rectProps.rotation })
        return { ...temp, x: -temp.x + 2, y: -temp.y + 1 }
    }, [rectProps.x, rectProps.y, rectProps.width, rectProps.height, rectProps.rotation, rectProps.realX, rectProps.realY])

    const fill = useMemo(() => {
        if (!(isSelected || rectProps.type != 'passenger' || !available))
            return undefined

        return isSelected && available && rectProps.type == 'passenger' ? theme.palette.success.main : theme.palette.grey['600']
    }, [isSelected, rectProps.type, available, theme])

    const onSelect = useCallback((e: KonvaEventObject<MouseEvent | Event>) => onSelectBase(e, shapeRef.current!), [onSelectBase]);

    return (
        <Group
            ref={shapeRef as any}
            x={rectProps.x}
            y={rectProps.y}
            rotation={rectProps.rotation}
            onClick={available && rectProps.type == 'passenger' ? onSelect : undefined}
            onTap={available && rectProps.type == 'passenger' ? onSelect : undefined}
            onMouseEnter={e => {
                if (!handleHover)
                    return

                e.target._clearCache()
                const container = e.target.getStage()?.container()
                if (!container)
                    return

                const pos = e.target.getAbsolutePosition()
                handleHover(rectProps, { ...pos, x: pos.x + container.offsetLeft + rectProps.width, y: pos.y + container.offsetTop + rectProps.height })
            }}
            onMouseLeave={_ => {
                if (handleHover)
                    handleHover(rectProps)
            }}>
            {rectProps.type == 'driver' ?
                <Image
                    {...rectProps}
                    x={0}
                    y={0}
                    width={rectProps.width}
                    height={rectProps.height}
                    rotation={undefined}
                    image={driverImage} /> :
                <>
                    {isSelected || rectProps.type != 'passenger' || !available ?
                        <Rect
                            strokeEnabled={false}
                            fill={fill}
                            x={0}
                            y={0}
                            width={rectProps.width}
                            height={rectProps.height}
                            rotation={undefined}
                            cornerRadius={10}
                        /> : null}
                    <Rect
                        stroke={theme.palette.grey['700']}
                        fill={theme.palette.grey['700']}
                        {...rectProps}
                        x={seatBackSide === 'bottom' ? 5 : 1}
                        y={seatBackSide === 'bottom'
                            ? Math.max(0, rectProps.height - Math.min(8, rectProps.height / 2) - 5)
                            : 5}
                        width={seatBackSide === 'bottom'
                            ? Math.max(2, rectProps.width - 10)
                            : Math.min(8, rectProps.width / 2)}
                        height={seatBackSide === 'bottom'
                            ? Math.min(8, rectProps.height / 2)
                            : Math.max(2, rectProps.height - 10)}
                        rotation={undefined}
                        cornerRadius={10} />
                    <Rect
                        strokeWidth={1}
                        stroke={theme.palette.text.primary}
                        {...rectProps}
                        x={0}
                        y={0}
                        width={rectProps.width}
                        height={rectProps.height}
                        rotation={undefined}
                        cornerRadius={10} />
                    <Text
                        text={rectProps.name}
                        {...textBox}
                        rotation={rectProps.rotation % 90 != 0 ? undefined : -rectProps.rotation}
                        fontSize={Math.min(rectProps.height, rectProps.width) / 2}
                        fill={fill ? theme.palette.getContrastText(fill) : theme.palette.text.primary}
                        align={'center'}
                        verticalAlign={'middle'} />
                </>}
        </Group>
    )
}
